/* eslint-disable max-lines-per-function */
import React from "react";
import { Stack, Typography } from "@mui/material";
import { AddCircleOutline } from "@mui/icons-material";
import { Button } from "components/haven-design/buttons";
import { BuilderDraft, BuilderSection, emptyDraftSection } from "./form-builder-utils";
import { QuestionGroupsBySection } from "./form-builder-shared";
import FormBuilderSectionCard from "./form-builder-section-card";

type Props = {
  sections: BuilderSection[];
  questionIdentityLabelMap: Record<string, string>;
  questionIdentityTypeMap: Record<string, string>;
  questionGroupsBySection: QuestionGroupsBySection;
  setDraft: React.Dispatch<React.SetStateAction<BuilderDraft>>;
};

const FormBuilderSectionsPanel: React.FC<Props> = ({
  sections,
  questionIdentityLabelMap,
  questionIdentityTypeMap,
  questionGroupsBySection,
  setDraft,
}) => {
  const updateSection = React.useCallback(
    (sectionId: string, update: (section: BuilderSection) => BuilderSection) => {
      setDraft((previous) => ({
        ...previous,
        sections: previous.sections.map((entry) => (
          entry.draftId === sectionId ? update(entry) : entry
        )),
      }));
    },
    [setDraft],
  );

  const deleteSection = React.useCallback((sectionId: string) => {
    setDraft((previous) => ({
      ...previous,
      sections: previous.sections.filter((entry) => entry.draftId !== sectionId),
    }));
  }, [setDraft]);

  const addSection = React.useCallback(() => {
    setDraft((previous) => ({
      ...previous,
      sections: [...previous.sections, emptyDraftSection()],
    }));
  }, [setDraft]);

  return (
    <>
      <Typography variant="h6" sx={{ mb: 1 }}>Sections</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        Only fully unassigned questions can be added. Child-linked questions stay outside sections.
      </Typography>
      <Stack spacing={2}>
        {sections.map((section) => (
          <FormBuilderSectionCard
            key={section.draftId}
            section={section}
            sections={sections}
            questionIdentityLabelMap={questionIdentityLabelMap}
            questionIdentityTypeMap={questionIdentityTypeMap}
            questionGroupsBySection={questionGroupsBySection}
            onUpdateSection={updateSection}
            onDeleteSection={deleteSection}
          />
        ))}

        <Button
          size="L"
          type="button"
          color="secondary"
          onClick={addSection}
        >
          <AddCircleOutline fontSize="small" style={{ marginRight: "0.3rem" }} />
          Add Section
        </Button>
      </Stack>
    </>
  );
};

export default FormBuilderSectionsPanel;
